import { ClassName } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { useQueryParams } from 'hooks';
import { t } from 'i18next';
import { useState } from 'react';
import { QueryParam } from 'utils/constants/queryParam';
import {
  getDocumentationRequiredFilterValue,
  getDocumentationRequiredPickerKey,
  getEmployeeCanManageFilterValue,
  getEmployeeCanManagePickerKey,
  getRenewalRequiredFilterValue,
  getRenewalRequiredPickerKey,
  getTrueFalseValueFromString,
} from '../utils';
import _, { isNil } from 'lodash';
import SidePanelFilter from 'components/foundation/FilterSidePanel/FilterSidePanel';
import BasicPicker from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import { documentationRequiredPickerData, employeeCanManagePickerData, renewalRequiredPickerData } from '../constants';

export interface IManageCertificationsFilterProps {
  className?: ClassName;
}

export interface IManageCertificationsFilterData {
  documentationRequired: boolean | null;
  renewalRequired: boolean | null;
  employeeCanManage: boolean | null;
}

const ManageCertificationsFilter = (props: IManageCertificationsFilterProps) => {
  const { className } = props;

  const { getParam, updateParams } = useQueryParams();

  const documentationRequiredParam = getParam(QueryParam.DOCUMENTATION_REQUIRED);
  const renewalRequiredParam = getParam(QueryParam.RENEWAL_REQUIRED);
  const employeeCanManageParam = getParam(QueryParam.EMPLOYEE_CAN_MANAGE);

  const [localFilterData, setLocalFilterData] = useState<IManageCertificationsFilterData>({
    documentationRequired: getTrueFalseValueFromString(documentationRequiredParam),
    renewalRequired: getTrueFalseValueFromString(renewalRequiredParam),
    employeeCanManage: getTrueFalseValueFromString(employeeCanManageParam),
  });

  function handleApply() {
    updateParams({
      [QueryParam.DOCUMENTATION_REQUIRED]: localFilterData.documentationRequired?.toString(),
      [QueryParam.RENEWAL_REQUIRED]: localFilterData.renewalRequired?.toString(),
      [QueryParam.EMPLOYEE_CAN_MANAGE]: localFilterData.employeeCanManage?.toString(),
    });
  }

  function handleReset() {
    setLocalFilterData({
      documentationRequired: null,
      renewalRequired: null,
      employeeCanManage: null,
    });

    updateParams({
      [QueryParam.DOCUMENTATION_REQUIRED]: null,
      [QueryParam.RENEWAL_REQUIRED]: null,
      [QueryParam.EMPLOYEE_CAN_MANAGE]: null,
    });
  }

  function hasChanged() {
    return (
      localFilterData.documentationRequired !== getTrueFalseValueFromString(documentationRequiredParam) ||
      localFilterData.renewalRequired !== getTrueFalseValueFromString(renewalRequiredParam) ||
      localFilterData.employeeCanManage !== getTrueFalseValueFromString(employeeCanManageParam)
    );
  }

  function anyFilterSet() {
    return _.some(
      Object.keys(localFilterData),
      (key: keyof IManageCertificationsFilterData) => !isNil(localFilterData[key])
    );
  }

  const classes = classNames('manage-certifications-filter', className);

  return (
    <SidePanelFilter
      appliable={hasChanged()}
      clearable={anyFilterSet()}
      onApply={handleApply}
      onClear={handleReset}
      className={classes}
      buttonsClassName="px-5"
    >
      <div className="px-5">
        <h3 className="my-5">{t('Filter')}</h3>
      </div>
      <div className="mx-6">
        <BasicPicker
          onSelect={(value) => {
            setLocalFilterData((previous) => ({
              ...previous,
              documentationRequired: getDocumentationRequiredFilterValue(value),
            }));
          }}
          value={getDocumentationRequiredPickerKey(localFilterData.documentationRequired)}
          data={documentationRequiredPickerData}
          placeholder={t('Documentation')}
          className="mb-4"
        />
        <BasicPicker
          onSelect={(value) => {
            setLocalFilterData((previous) => ({
              ...previous,
              renewalRequired: getRenewalRequiredFilterValue(value),
            }));
          }}
          value={getRenewalRequiredPickerKey(localFilterData.renewalRequired)}
          data={renewalRequiredPickerData}
          placeholder={t('Renewal')}
          className="mb-4"
        />
        <BasicPicker
          onSelect={(value) => {
            setLocalFilterData((previous) => ({
              ...previous,
              employeeCanManage: getEmployeeCanManageFilterValue(value),
            }));
          }}
          value={getEmployeeCanManagePickerKey(localFilterData.employeeCanManage)}
          data={employeeCanManagePickerData}
          placeholder={t('Permissions')}
          className="mb-4"
        />
      </div>
    </SidePanelFilter>
  );
};

export default ManageCertificationsFilter;
